import config from '../config';
import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { User, Lock, ShieldCheck, KeyRound, ArrowRight, Loader2, AlertTriangle, XCircle } from 'lucide-react';
import logo from '../assets/nesr-logo.png';
import { ThemeToggle } from '../theme';

const Login = ({ onLogin }) => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  // التأكيد بخطوتين للمديرين: 'password' ← 'otp' (كود من الموبايل) أو 'setup' (أول ربط للموبايل)
  const [step, setStep] = useState('password');
  const [otp, setOtp] = useState({ ticket: '', secret: '', url: '', code: '' });

  useEffect(() => {
    const checkConnection = async () => {
      try {
        const response = await fetch(`${config.API_BASE_URL}/test`, { method: 'GET' });
        setConnectionStatus(response.ok ? 'ok' : 'fail');
      } catch {
        setConnectionStatus('fail');
      }
    };
    checkConnection();
  }, []);

  const finishLogin = (user) => {
    localStorage.setItem('user', JSON.stringify(user));
    const permissions = user.Permissions ? JSON.parse(user.Permissions) : {};
    localStorage.setItem('userPermissions', JSON.stringify(permissions));
    localStorage.setItem('userId', user.UserId);
    localStorage.setItem('fullName', user.FullName);
    onLogin(user);
  };

  // الخطوة التانية: الكود اللي في تطبيق التأكيد على الموبايل
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/login/${step === 'setup' ? 'otp-setup' : 'otp'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticket: otp.ticket, code: otp.code.replace(/\s/g, '') })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.success) return finishLogin(data.user);
      setError(data.message || 'الكود غلط');
      if (response.status === 401 && /الوقت خلص/.test(data.message || '')) { setStep('password'); setOtp({ ticket: '', secret: '', url: '', code: '' }); }
    } catch {
      setError('حدث خطأ في الاتصال بالخادم');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });
      if (!response.ok) {
        // رسالة السيرفر نفسه (كلمة مرور غلط، محاولات كتير، السيستم بيبدأ...)
        const failed = await response.json().catch(() => null);
        if (failed && (failed.message || failed.error)) { setError(failed.message || failed.error); return; }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data.success) {
        finishLogin(data.user);
      } else if (data.otpRequired || data.otpSetupRequired) {
        setStep(data.otpSetupRequired ? 'setup' : 'otp');
        setOtp({ ticket: data.ticket, secret: data.secret || '', url: data.otpauthUrl || '', code: '' });
      } else {
        setError(data.message || 'خطأ في تسجيل الدخول');
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError(`تعذّر الوصول إلى الخادم — تأكد من تشغيل الخادم`);
      } else {
        setError('حدث خطأ في الاتصال بالخادم');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetOtp = () => { setStep('password'); setError(''); setOtp({ ticket: '', secret: '', url: '', code: '' }); };
  const inputCls = 'w-full h-12 rounded-xl border border-gray-300 bg-white pr-11 pl-4 text-gray-900 placeholder-gray-400 outline-none transition focus:border-gold-500 focus:ring-4 focus:ring-gold-500/15 disabled:opacity-60';
  const goldBtn = 'w-full h-12 rounded-xl font-bold text-[#0f1d3d] bg-gradient-to-l from-[#e8cb7d] via-[#d4ab4c] to-[#b98a2c] shadow-[0_8px_20px_-8px_rgba(185,138,44,.7)] hover:brightness-105 active:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed transition flex items-center justify-center gap-2';

  return (
    <div dir="rtl" className="min-h-screen flex bg-[#f4f6fa] dark:bg-[#0a1222]">
      {/* لوحة الهوية */}
      <aside className="relative hidden lg:flex lg:w-[46%] xl:w-1/2 flex-col items-center justify-center overflow-hidden text-white bg-gradient-to-br from-[#0f1d3d] via-[#13254d] to-[#0a142b] px-12">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '22px 22px' }} />
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full border border-[#d4ab4c]/20" />
        <div className="absolute -bottom-52 -right-40 w-[560px] h-[560px] rounded-full border border-[#d4ab4c]/10" />
        <div className="relative flex flex-col items-center text-center max-w-md">
          <div className="w-44 h-44 rounded-full bg-[#fff] shadow-[0_20px_60px_-15px_rgba(0,0,0,.6)] ring-4 ring-[#d4ab4c]/40 flex items-center justify-center">
            <img src={logo} alt="نسر الخليج" className="w-36 h-36 object-contain" />
          </div>
          <h1 className="mt-8 text-4xl font-black tracking-tight">نسر الخليج</h1>
          <p className="mt-2 text-[#e0c070] font-semibold">لإلحاق العمالة المصرية بالخارج</p>
          <div className="mt-5 h-1 w-16 rounded-full bg-gradient-to-l from-[#e8cb7d] to-[#b98a2c]" />
          <p className="mt-5 text-lg text-white/80">نظام إدارة الجوازات والتأشيرات</p>
          <div className="mt-10 grid grid-cols-2 gap-3 w-full text-sm">
            {['إدارة الجوازات والوثائق', 'صلاحيات لكل مستخدم', 'تسجيل كامل للعمليات', 'حماية متقدمة للبيانات'].map((t) => (
              <div key={t} className="flex items-center gap-2 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-white/85">
                <ShieldCheck className="w-4 h-4 text-[#d4ab4c] shrink-0" />
                <span>{t}</span>
              </div>
            ))}
          </div>
          <div className="mt-10 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-1.5 text-xs text-white/70">
            <ShieldCheck className="w-3.5 h-3.5 text-[#d4ab4c]" />
            ترخيص وزارة القوى العاملة المصرية رقم <b className="text-[#e0c070]">863</b>
          </div>
        </div>
      </aside>

      {/* نموذج الدخول */}
      <main className="relative flex-1 flex items-center justify-center px-4 py-10">
        <div className="absolute top-4 left-4 flex items-center gap-2">
          <ThemeToggle />
        </div>
        {connectionStatus && (
          <div className={`absolute top-5 right-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border ${connectionStatus === 'ok' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
            <span className={`w-2 h-2 rounded-full ${connectionStatus === 'ok' ? 'bg-green-500' : 'bg-red-500'}`} />
            {connectionStatus === 'ok' ? 'متصل' : 'غير متصل'}
          </div>
        )}

        <div className="w-full max-w-md">
          <div className="lg:hidden flex flex-col items-center mb-6 text-center">
            <div className="w-24 h-24 rounded-full bg-[#fff] shadow-lg ring-2 ring-gold-400/50 flex items-center justify-center">
              <img src={logo} alt="نسر الخليج" className="w-20 h-20 object-contain" />
            </div>
            <div className="mt-3 text-2xl font-black text-navy-900">نسر الخليج</div>
            <div className="text-sm text-gold-700 font-semibold">نظام إدارة الجوازات</div>
          </div>

          <div className="rounded-3xl bg-white border border-gray-200 shadow-[0_24px_60px_-30px_rgba(15,29,61,.45)] p-7 sm:p-9">
            <div className="text-center mb-7">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 text-navy-700 border border-navy-100 px-3 py-1 text-xs font-semibold">
                <ShieldCheck className="w-3.5 h-3.5" /> دخول آمن
              </div>
              <h2 className="mt-3 text-2xl font-black text-gray-900">{step === 'password' ? 'تسجيل الدخول' : step === 'setup' ? 'ربط الموبايل' : 'كود التأكيد'}</h2>
              <p className="mt-1 text-sm text-gray-500">{step === 'password' ? 'أدخل بياناتك للدخول على السيستم' : 'خطوة تأكيد إضافية لحماية حساب المدير'}</p>
            </div>

            {connectionStatus === 'fail' && (
              <div className="mb-4 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800">
                <AlertTriangle className="w-5 h-5 shrink-0" />
                <div>الخادم غير متاح حالياً<div className="text-xs opacity-80">تأكد من اتصال الإنترنت وجرّب تاني بعد شوية</div></div>
              </div>
            )}
            {error && (
              <div className="mb-4 flex gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700" role="alert">
                <XCircle className="w-5 h-5 shrink-0" />
                <div>{error}</div>
              </div>
            )}

            {step !== 'password' ? (
              <form onSubmit={handleOtpSubmit} className="space-y-4">
                {step === 'setup' && (
                  <div className="space-y-3 text-sm leading-7 text-gray-600">
                    <div>1. نزّل على موبايلك تطبيق <b className="text-gray-900">Google Authenticator</b> أو <b className="text-gray-900">Microsoft Authenticator</b>.</div>
                    <div>2. افتح التطبيق ودوس <b className="text-gray-900">+</b> وبعدين <b className="text-gray-900">مسح كود QR</b>، وصوّر الكود ده:</div>
                    <div className="flex justify-center"><div className="rounded-2xl bg-[#fff] p-2 border border-gray-200"><QRCodeSVG value={otp.url} size={168} includeMargin /></div></div>
                    <div className="text-center text-xs text-gray-500">أو اكتب الكود ده في التطبيق يدوي:
                      <code dir="ltr" className="block mt-1 font-mono tracking-wider text-gray-900 bg-gray-100 rounded-lg px-2 py-1">{otp.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
                    </div>
                    <div>3. اكتب الـ 6 أرقام اللي ظهروا في التطبيق:</div>
                  </div>
                )}
                {step === 'otp' && (
                  <p className="text-sm leading-7 text-gray-600">افتح تطبيق التأكيد على موبايلك واكتب الـ 6 أرقام الظاهرين دلوقتي.</p>
                )}
                <div>
                  <label className="block mb-1.5 text-sm font-semibold text-gray-700">كود التأكيد</label>
                  <div className="relative">
                    <KeyRound className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold`}
                      dir="ltr"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={7}
                      placeholder="000000"
                      value={otp.code}
                      onChange={(e) => setOtp({ ...otp, code: e.target.value.replace(/[^\d]/g, '').slice(0, 6) })}
                      autoFocus
                      disabled={loading}
                      required
                    />
                  </div>
                </div>
                <button type="submit" className={goldBtn} disabled={loading || otp.code.length !== 6}>
                  {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />جاري التحقق</>) : 'تأكيد'}
                </button>
                <button type="button" className="w-full h-10 rounded-xl text-sm font-semibold text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition flex items-center justify-center gap-1" onClick={resetOtp}>
                  <ArrowRight className="w-4 h-4" /> رجوع
                </button>
              </form>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="login-user" className="block mb-1.5 text-sm font-semibold text-gray-700">اسم المستخدم</label>
                  <div className="relative">
                    <User className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="login-user"
                      className={inputCls}
                      type="text"
                      autoComplete="username"
                      placeholder="أدخل اسم المستخدم"
                      value={credentials.username}
                      onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                      disabled={loading}
                      autoFocus
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="login-pass" className="block mb-1.5 text-sm font-semibold text-gray-700">كلمة المرور</label>
                  <div className="relative">
                    <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      id="login-pass"
                      className={inputCls}
                      type="password"
                      autoComplete="current-password"
                      placeholder="أدخل كلمة المرور"
                      value={credentials.password}
                      onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                      disabled={loading}
                      required
                    />
                  </div>
                </div>
                <button type="submit" className={`${goldBtn} mt-2`} disabled={loading}>
                  {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />جاري التحقق</>) : 'دخول إلى السيستم'}
                </button>
              </form>
            )}

            <div className="mt-7 pt-5 border-t border-gray-100 text-center text-xs text-gray-400">
              جميع العمليات مُسجَّلة ومحمية · {new Date().getFullYear()}
            </div>
          </div>
          <p className="mt-5 text-center text-xs text-gray-400">© {new Date().getFullYear()} نسر الخليج لإلحاق العمالة المصرية بالخارج</p>
        </div>
      </main>
    </div>
  );
};
export default Login;
